import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, fmtMoney, fmtDateRelative } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, CreditCard, Sparkles, Tag, AlertTriangle, ReceiptText, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const STATUS_LABEL = {
    trialing: "Trial",
    active: "Active",
    past_due: "Past due",
    pending: "Pending",
    disabled: "Disabled",
    cancelled: "Cancelled",
};

const STATUS_BADGE = {
    trialing: "bg-gold/15 text-gold border-gold/40",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    past_due: "bg-orange-50 text-orange-700 border-orange-300",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    disabled: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

export default function Billing() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const nav = useNavigate();
    const [sub, setSub] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [discountCode, setDiscountCode] = useState("");
    const [discountPreview, setDiscountPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        const [s, p] = await Promise.all([api.get("/billing/my-subscription"), api.get("/billing/plans")]);
        setSub(s.data);
        setPlans(p.data);
    };

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, []);

    // If we just returned from Stripe, poll status
    const sessionId = params.get("session_id");
    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        let cancelled = false;
        const poll = async () => {
            attempts++;
            try {
                const { data } = await api.get(`/billing/checkout/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid" || data.status === "completed") {
                    toast.success("Payment successful — subscription activated");
                    setParams({});
                    refresh();
                    return;
                }
                if (data.stripe_status === "expired") {
                    toast.error("Checkout session expired");
                    setParams({});
                    return;
                }
                if (attempts < 7) setTimeout(poll, 2000);
                else {
                    toast.message("Still waiting on Stripe — refresh in a minute.");
                    setParams({});
                }
            } catch {
                if (attempts < 5) setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]); // eslint-disable-line

    if (loading || !sub) return <div className="p-8 text-sm text-muted-foreground">Loading billing…</div>;

    if (user?.role === "super_admin") {
        return (
            <div className="p-8" data-testid="billing-page-super-admin">
                <p className="text-sm text-muted-foreground">Super-admin accounts have no subscription. Manage tenants via <a href="/super-admin" className="underline">/super-admin</a>.</p>
            </div>
        );
    }

    const c = sub.company;
    const currentPlan = sub.plan;
    const days = sub.days_remaining;

    // Partner comp plan — replace billing UI entirely
    if (c?.plan_type === "partner_comp") {
        return (
            <div data-testid="billing-page">
                <PageHeader title="Billing" subtitle={`${c.name} · Partner`}>
                    <span
                        data-testid="billing-partner-badge"
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest"
                        style={{ background: 'rgba(201,162,39,0.12)', color: '#C9A227', border: '1px solid rgba(201,162,39,0.35)' }}
                    >
                        Partner
                    </span>
                </PageHeader>
                <div className="p-6 lg:p-8 max-w-2xl">
                    <div className="border border-[#C9A227]/30 rounded-md bg-[#FFFDF5] p-6 space-y-4" data-testid="partner-comp-panel">
                        <div className="flex items-start gap-3">
                            <Sparkles className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                            <div>
                                <p className="font-display font-bold text-lg text-navy">Plan: Partner (Complimentary)</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Full Pro access, granted as part of the {c.comp_reason || "M&A advisory"} partnership. No billing applies.
                                </p>
                            </div>
                        </div>
                        <div className="border-t border-[#C9A227]/20 pt-4 grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Granted by</div>
                                <div className="mt-0.5 font-medium">{c.granted_by || "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expires</div>
                                <div className="mt-0.5 font-medium">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Indefinite"}</div>
                            </div>
                        </div>
                        <div className="border-t border-[#C9A227]/20 pt-4">
                            <a
                                href="mailto:support@closvio.com"
                                className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                                data-testid="partner-contact-support"
                            >
                                Contact support
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const validateDiscount = async () => {
        if (!discountCode.trim()) return setDiscountPreview(null);
        try {
            const { data } = await api.get(`/billing/discount/${discountCode.trim().toUpperCase()}`);
            setDiscountPreview(data);
            toast.success(`${data.percent_off}% off applied`);
        } catch (err) {
            setDiscountPreview(null);
            toast.error(err.response?.data?.detail || "Invalid code");
        }
    };

    const startCheckout = async (planSlug) => {
        setBusy(true);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan_slug: planSlug,
                discount_code: discountPreview ? discountPreview.code : null,
                origin_url: window.location.origin,
            });
            if (data.url) {
                window.location.href = data.url;
            } else if (data.redirect_url) {
                window.location.href = data.redirect_url;
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
            setBusy(false);
        }
    };

    const cancel = async () => {
        if (!confirm("Cancel your subscription and downgrade to Free?")) return;
        await api.post("/billing/cancel");
        toast.success("Subscription cancelled");
        refresh();
    };

    return (
        <div data-testid="billing-page">
            <PageHeader title="Billing" subtitle={`${c.name} · ${STATUS_LABEL[c.status]}`}>
                <Badge className={`text-[10px] uppercase tracking-wider ${STATUS_BADGE[c.status]}`}>{c.status}</Badge>
            </PageHeader>

            <div className="p-6 lg:p-8 space-y-6 max-w-5xl">
                {(c.status === "past_due" || c.status === "disabled" || c.status === "pending") && (
                    <div className="border border-orange-300 bg-orange-50/60 rounded-md p-4 flex items-start gap-3" data-testid="billing-alert">
                        <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-orange-900">
                                {c.status === "past_due" ? "Your subscription is past due." :
                                 c.status === "disabled" ? "Your account has been disabled." :
                                 "Your account is awaiting approval."}
                            </p>
                            <p className="text-xs text-orange-700 mt-1">
                                {c.status === "disabled" ? "Contact support to reactivate." : "Choose a plan below to continue using the app."}
                            </p>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Stat label="Current plan" value={currentPlan?.name || "—"} accent testid="billing-current-plan" />
                    <Stat label={c.status === "trialing" ? "Trial ends" : "Period ends"} value={
                        days !== null && days !== undefined ? `${days} days` : "—"
                    } mono testid="billing-days" />
                    <Stat label="Monthly price" value={fmtMoney(currentPlan?.price_monthly || 0, currentPlan?.currency || "GBP")} mono testid="billing-price" />
                </div>

                {c.discount_percent > 0 && (
                    <div className="border border-gold/40 bg-gold/10 rounded-md px-4 py-3 text-sm text-foreground/90" data-testid="standing-discount">
                        <Tag className="w-4 h-4 text-gold inline mr-1.5" /> You have a standing <b>{c.discount_percent}% discount</b> applied to all future invoices.
                    </div>
                )}

                {/* Plan grid */}
                <div>
                    <h2 className="font-display text-lg font-bold tracking-tight mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gold" /> Choose a plan
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
                        {plans.map((p, i) => {
                            const isCurrent = c.plan_slug === p.slug;
                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05, duration: 0.3 }}
                                    className={`border rounded-md bg-white p-5 flex flex-col ${isCurrent ? "border-gold ring-2 ring-gold/20" : "border-border"}`}
                                    data-testid={`plan-card-${p.slug}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-display font-bold text-xl">{p.name}</h3>
                                        {isCurrent && <Badge className="bg-gold text-foreground text-[9px] uppercase">Current</Badge>}
                                    </div>
                                    <div className="mt-3 flex items-baseline gap-1">
                                        <span className="text-3xl font-bold font-mono text-navy">{fmtMoney(p.price_monthly, p.currency)}</span>
                                        <span className="text-xs text-muted-foreground">/ month</span>
                                    </div>
                                    <ul className="mt-4 space-y-1.5 text-xs flex-1">
                                        {(p.features || []).map((f, idx) => (
                                            <li key={idx} className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-gold mt-0.5 shrink-0" />{f}</li>
                                        ))}
                                    </ul>
                                    <Button
                                        disabled={busy || (isCurrent && c.status === "active")}
                                        className={`mt-5 w-full ${isCurrent ? "bg-muted text-foreground" : "bg-foreground text-background hover:bg-neutral-800 hover:text-white"}`}
                                        onClick={() => p.slug === "free" ? cancel() : startCheckout(p.slug)}
                                        data-testid={`subscribe-${p.slug}-btn`}
                                    >
                                        {isCurrent && c.status === "active" ? "Current plan" :
                                         p.slug === "free" ? "Downgrade to Free" :
                                         busy ? "Processing…" : (
                                            <>{c.status === "active" ? "Switch to" : "Subscribe to"} {p.name} <Sparkles className="w-3.5 h-3.5 ml-1.5" /></>
                                         )}
                                    </Button>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Discount input */}
                <div className="border border-border rounded-md bg-white p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Tag className="w-4 h-4 text-gold" />
                        <h3 className="font-display font-bold tracking-tight">Have a discount code?</h3>
                    </div>
                    <div className="flex gap-2 max-w-md">
                        <Input
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            placeholder="LAUNCH20"
                            className="font-mono uppercase"
                            data-testid="discount-code-input"
                        />
                        <Button onClick={validateDiscount} variant="outline" data-testid="apply-discount-btn">Apply</Button>
                    </div>
                    {discountPreview && (
                        <p className="text-xs text-emerald-700 mt-2">
                            ✓ <b>{discountPreview.code}</b> · {discountPreview.percent_off}% off — applies on checkout.
                        </p>
                    )}
                </div>

                {/* Recent transactions */}
                <div className="border border-border rounded-md bg-white p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-bold tracking-tight flex items-center gap-1.5">
                            <ReceiptText className="w-4 h-4 text-gold" /> Recent invoices
                        </h3>
                        <Button size="sm" variant="ghost" onClick={refresh} data-testid="refresh-billing-btn">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                    {sub.transactions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">No invoices yet.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                    <th className="text-left py-2">Date</th>
                                    <th className="text-right py-2">Amount</th>
                                    <th className="text-left py-2 pl-4">Plan</th>
                                    <th className="text-left py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sub.transactions.map((t) => (
                                    <tr key={t.id} className="border-b border-border/60 last:border-0" data-testid={`txn-${t.id}`}>
                                        <td className="py-2.5 text-xs font-mono text-muted-foreground">{fmtDateRelative(t.created_at)}</td>
                                        <td className="py-2.5 text-right font-mono font-medium text-navy">{fmtMoney(t.amount, t.currency)}</td>
                                        <td className="py-2.5 pl-4 capitalize text-xs">{t.plan_slug || "—"}</td>
                                        <td className="py-2.5">
                                            <Badge variant="outline" className={`text-[10px] capitalize ${t.payment_status === "paid" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : t.payment_status === "granted" ? "text-gold border-gold/30 bg-gold/10" : ""}`}>
                                                {t.payment_status || t.status}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, mono, accent, testid }) {
    return (
        <div className="border border-border rounded-md bg-white p-4" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`mt-1 text-2xl font-bold ${mono ? "font-mono text-navy" : "font-display"} ${accent ? "text-gold" : ""}`}>{value}</div>
        </div>
    );
}
